import { mat3, ReadonlyMat3 } from 'gl-matrix';

/** The anatomical planes a view can show. */
export enum Orientations {
    TRANSVERSAL_ORIENTATION = 'TRANSVERSAL_ORIENTATION',
    SAGITTAL_ORIENTATION = 'SAGITTAL_ORIENTATION',
    CORONAL_ORIENTATION = 'CORONAL_ORIENTATION',
}

/** Display names for the planes, for error messages and UI keys. */
export const PLANE_NAMES: Readonly<Record<Orientations, string>> = {
    [Orientations.TRANSVERSAL_ORIENTATION]: 'transversal',
    [Orientations.SAGITTAL_ORIENTATION]: 'sagittal',
    [Orientations.CORONAL_ORIENTATION]: 'coronal',
};

/**
 * Image-grid axes per plane: `u`/`v` in-plane (field index `v * width + u`),
 * `k` the normal.
 */
export const PLANE_AXES: Readonly<
    Record<
        Orientations,
        { readonly u: 0 | 1 | 2; readonly v: 0 | 1 | 2; readonly k: 0 | 1 | 2 }
    >
> = {
    [Orientations.TRANSVERSAL_ORIENTATION]: { u: 0, v: 1, k: 2 },
    [Orientations.SAGITTAL_ORIENTATION]: { u: 1, v: 2, k: 0 },
    [Orientations.CORONAL_ORIENTATION]: { u: 0, v: 2, k: 1 },
};

/**
 * Object type describing a dictionary mapping {@link Orientations} to a
 * {@link mat3} transformation matrix.
 */
export type OrientationDictionnary = {
    [key in Orientations]: mat3;
};

/**
 * What {@link computeViewOrientation} reads off an image grid: its
 * orientation and whether it lies along the patient axes.
 */
export interface ViewOrientationGrid {
    getOrientation(): mat3;
    isAxisAligned(): boolean;
}

/**
 * The view-to-patient rotation of each standard anatomical plane for a
 * head-first supine patient, column-major: the columns are the in-plane x
 * and y axes and the view normal.
 *
 * - Transversal: view x along patient x, view y inverted along patient y,
 *   view z inverted along patient z.
 * - Coronal (frontal): view x along patient x, view y along patient z,
 *   view z inverted along patient y.
 * - Sagittal: view x along patient y, view y along patient z, view z
 *   inverted along patient x.
 */
const STANDARD_VIEW_ORIENTATIONS: Readonly<Record<Orientations, ReadonlyMat3>> =
    {
        [Orientations.TRANSVERSAL_ORIENTATION]: mat3.fromValues(
            1.0,
            0.0,
            0.0,
            0.0,
            -1.0,
            0.0,
            0.0,
            0.0,
            -1.0
        ),
        [Orientations.CORONAL_ORIENTATION]: mat3.fromValues(
            1.0,
            0.0,
            0.0,
            0.0,
            0.0,
            1.0,
            0.0,
            -1.0,
            0.0
        ),
        [Orientations.SAGITTAL_ORIENTATION]: mat3.fromValues(
            0.0,
            -1.0,
            0.0,
            0.0,
            0.0,
            1.0,
            -1.0,
            0.0,
            0.0
        ),
    };

/**
 * The standard view-to-patient rotation of the `view` plane, a fresh matrix
 * per call so a caller may turn it in place.
 */
export function standardViewOrientation(view: Orientations): mat3 {
    return mat3.clone(STANDARD_VIEW_ORIENTATIONS[view]);
}

/** {@link standardViewOrientation} for every plane, fresh matrices per call. */
export function standardViewOrientations(): OrientationDictionnary {
    return {
        [Orientations.TRANSVERSAL_ORIENTATION]: standardViewOrientation(
            Orientations.TRANSVERSAL_ORIENTATION
        ),
        [Orientations.SAGITTAL_ORIENTATION]: standardViewOrientation(
            Orientations.SAGITTAL_ORIENTATION
        ),
        [Orientations.CORONAL_ORIENTATION]: standardViewOrientation(
            Orientations.CORONAL_ORIENTATION
        ),
    };
}

/**
 * The view-to-patient rotation of the `view` anatomical plane on an image,
 * derived from the image grid.
 *
 * For axis-aligned images, the four transversal patient positions (HFS,
 * HFP, FFS, FFP) and 90°-rotated coronal and sagittal acquisitions, the
 * result is `imageOrientation * standardView`, which transforms the standard
 * view axes into the image's own frame.
 *
 * For oblique (tilted) images it falls back to the standard matrix.
 *
 * View-free on purpose: the slice geometry of a view is decided from this
 * without the view class, so no module that a view depends on has to depend
 * on the view.
 */
export function computeViewOrientation(
    imageGrid: ViewOrientationGrid,
    view: Orientations
): mat3 {
    if (!imageGrid.isAxisAligned()) {
        return standardViewOrientation(view);
    }
    return mat3.multiply(
        mat3.create(),
        imageGrid.getOrientation(),
        standardViewOrientation(view)
    );
}
